<template>
  <a-modal
    v-model:visible="visible"
    :title="t('workspaceSettings.identifiers.targetModal.title')"
    width="800px"
    :on-before-ok="handleBeforeOk"
    @cancel="handleCancel"
  >
    <div class="update-section">
      <div class="section-title">{{
        t('workspaceSettings.identifiers.targetModal.latestTarget')
      }}</div>
      <div class="target-card">
        <template v-if="latestTarget">
          <div class="icon-wrapper">
            <img src="@/assets/images/workflow.png" alt="" />
          </div>
          <div class="info">
            <div class="name">{{ latestTarget.workflowName }}</div>
            <div class="desc">{{ latestTarget.workflowDesc }}</div>
          </div>
          <a-tag v-if="latestTarget.version" color="green" size="small">{{
            latestTarget.version
          }}</a-tag>
        </template>
        <template v-else>
          <div class="icon-wrapper">
            <img src="@/assets/images/workflow.png" alt="" />
          </div>
          <div class="info">
            <div class="name" style="color: var(--color-text-3)">
              {{ t('workspaceSettings.identifiers.targetModal.noTarget') }}
            </div>
          </div>
        </template>
      </div>
    </div>

    <div class="update-section">
      <div class="section-title">{{
        t('workspaceSettings.identifiers.targetModal.updateTo')
      }}</div>

      <div class="selection-container">
        <!-- Target Workflow Column -->
        <div class="column">
          <div class="column-header">{{
            t('workspaceSettings.identifiers.targetModal.targetWorkflow')
          }}</div>
          <div class="card-list-wrapper">
            <a-spin
              :loading="loadingWorkflows"
              style="width: 100%; height: 100%"
            >
              <div class="card-list">
                <div
                  v-for="wf in workflowList"
                  :key="wf.workflow_uuid"
                  class="card-item"
                  :class="{ active: selectedWorkflowUuid === wf.workflow_uuid }"
                  @click="handleWorkflowChange(wf.workflow_uuid)"
                >
                  <div class="card-icon">
                    <img
                      src="@/assets/images/workflow.png"
                      class="icon"
                      alt=""
                    />
                  </div>
                  <div class="card-content">
                    <div class="card-title">{{ wf.name }}</div>
                    <div class="card-desc">{{ wf.description || '-' }}</div>
                    <div class="card-meta">
                      {{
                        t('workspaceSettings.identifiers.targetModal.updatedAt')
                      }}
                      {{ formatDate(wf.updated_at) }}
                    </div>
                  </div>
                </div>
                <a-empty
                  v-if="!loadingWorkflows && workflowList.length === 0"
                />
              </div>
            </a-spin>
          </div>
        </div>

        <!-- Target Version Column -->
        <div class="column">
          <div class="column-header">{{
            t('workspaceSettings.identifiers.targetModal.targetVersion')
          }}</div>
          <div class="card-list-wrapper">
            <a-spin
              :loading="loadingVersions"
              style="width: 100%; height: 100%"
            >
              <div class="card-list">
                <template v-if="selectedWorkflowUuid">
                  <div
                    v-for="ver in versionList"
                    :key="ver.history_uuid"
                    class="card-item"
                    :class="{ active: selectedVersion === ver.publish_version }"
                    @click="selectedVersion = ver.publish_version"
                  >
                    <div class="card-content full-width">
                      <div class="card-top-row">
                        <a-tag color="green" size="small">{{
                          ver.publish_version
                        }}</a-tag>
                      </div>
                      <div class="card-desc">{{
                        ver.description ||
                        t(
                          'workspaceSettings.identifiers.targetModal.versionDesc'
                        )
                      }}</div>
                      <div class="card-meta">
                        {{
                          t(
                            'workspaceSettings.identifiers.targetModal.updatedAt'
                          )
                        }}
                        {{ formatDate(ver.updated_at) }}
                        {{ t('workspaceSettings.identifiers.targetModal.by') }}
                        {{ ver.editor }}
                      </div>
                    </div>
                  </div>
                  <a-empty
                    v-if="!loadingVersions && versionList.length === 0"
                    description="No published versions found"
                  />
                </template>
                <div v-else class="placeholder-text">
                  {{
                    t(
                      'workspaceSettings.identifiers.targetModal.selectWorkflow'
                    )
                  }}
                </div>
              </div>
            </a-spin>
          </div>
        </div>
      </div>
    </div>
  </a-modal>
</template>

<script setup lang="ts">
  import { ref, reactive, computed, watch, onMounted } from 'vue';
  import Message from '@/utils/message';
  import dayjs from 'dayjs';
  import { useI18n } from 'vue-i18n';
  import { updateProtocolTargetApi } from '@/api/protocol';
  import { listWorkflowsApi } from '@/api/workflow';
  import { listWorkflowHistoryApi } from '@/api/workflow-version';
  import type { ProtocolItem } from '@/types/protocol';
  import type { WorkflowDefinition, WorkflowVersion } from '@/types/workflow';
  import { useUserStore } from '@/store';

  const props = defineProps<{
    data: ProtocolItem | null;
  }>();

  const emit = defineEmits<{
    (e: 'success'): void;
  }>();

  const visible = defineModel('visible', { type: Boolean, default: false });

  const { t } = useI18n();
  const userStore = useUserStore();
  const loadingWorkflows = ref(false);
  const loadingVersions = ref(false);
  const workflowList = ref<WorkflowDefinition[]>([]);
  const versionList = ref<WorkflowVersion[]>([]);

  const selectedWorkflowUuid = ref('');
  const selectedVersion = ref('');

  const latestTarget = computed(() => {
    if (!props.data?.target_workflow_uuid) return null;
    return {
      workflowName:
        props.data.target_workflow_name ||
        t('workspaceSettings.identifiers.targetModal.unknownWorkflow'),
      workflowDesc: props.data.target_workflow_description || '',
      version: props.data.target_publish_version,
    };
  });

  const formatDate = (date?: string) => {
    if (!date) return '-';
    return dayjs(date).format('DD/MM/YYYY HH:mm:ss');
  };

  const fetchWorkflows = async () => {
    loadingWorkflows.value = true;
    try {
      const res = await listWorkflowsApi({
        tenant_uuid: userStore.tenantUuid!,
        workspace_uuid: userStore.workspaceUuid!,
        pagination: { page_no: 1, page_size: 1000 }, // Fetch all ideally
      });
      if (res.data?.results) {
        workflowList.value = res.data.results;
      }
    } catch (error) {
      console.error(error);
    } finally {
      loadingWorkflows.value = false;
    }
  };

  const handleWorkflowChange = async (workflowUuid: string) => {
    if (selectedWorkflowUuid.value === workflowUuid) return;

    selectedWorkflowUuid.value = workflowUuid;
    selectedVersion.value = '';
    versionList.value = [];

    loadingVersions.value = true;
    try {
      const res = await listWorkflowHistoryApi({
        tenant_uuid: userStore.tenantUuid!,
        workspace_uuid: userStore.workspaceUuid!,
        filters: {
          workflow_uuid: workflowUuid,
          is_publish: true, // Only published versions? PRD says "Target workflow的所有发布版本"
        },
        pagination: { page_no: 1, page_size: 100 },
      });
      if (res.data?.results) {
        versionList.value = res.data.results;
      }
    } catch (error) {
      console.error(error);
    } finally {
      loadingVersions.value = false;
    }
  };

  watch(
    () => visible.value,
    (val) => {
      if (val) {
        fetchWorkflows();
        // Reset Selection
        selectedWorkflowUuid.value = '';
        selectedVersion.value = '';
        versionList.value = [];
      }
    }
  );

  const handleCancel = () => {
    visible.value = false;
  };

  const handleBeforeOk = async () => {
    if (!props.data) return false;
    if (!selectedWorkflowUuid.value || !selectedVersion.value) {
      Message.error(
        t('workspaceSettings.identifiers.targetModal.selectRequired')
      );
      return false;
    }

    try {
      await updateProtocolTargetApi({
        name: props.data.name,
        description: props.data.description,
        tenant_uuid: userStore.tenantUuid!,
        workspace_uuid: userStore.workspaceUuid!,
        target_workflow_uuid: selectedWorkflowUuid.value,
        target_publish_version: selectedVersion.value,
      });
      Message.success(
        t('workspaceSettings.identifiers.message.targetUpdatedSuccess')
      );
      emit('success');
      return true;
    } catch (error) {
      return false;
    }
  };
</script>

<style lang="less" scoped>
  .update-section {
    margin-bottom: 24px;
    &:last-child {
      margin-bottom: 0;
    }

    .section-title {
      font-weight: 600;
      margin-bottom: 12px;
      color: var(--color-text-1);
    }

    .target-card {
      display: flex;
      align-items: center;
      padding: 12px 24px;
      border: 1px solid var(--color-border-2);
      border-radius: 4px;
      background: var(--color-bg-2);
      gap: 12px;

      .icon-wrapper {
        padding: 16px 20px;
        background: #fff;
        border: 1px solid var(--color-border-2);
        border-radius: var(--border-radius-small);
        img {
          width: 36px;
          height: 36px;
        }
      }

      .info {
        flex: 1;
        .name {
          font-weight: 500;
          color: var(--color-text-1);
        }
        .desc {
          font-size: 12px;
          color: var(--color-text-3);
        }
      }
    }
  }

  .selection-container {
    display: flex;
    gap: 16px;
    height: 480px;

    .column {
      flex: 1;
      display: flex;
      flex-direction: column;
      min-width: 0;

      .column-header {
        color: var(--color-text-2);
        margin-bottom: 8px;
      }

      .card-list-wrapper {
        flex: 1;
        border: 1px solid var(--color-border-2);
        border-radius: 4px;
        background: var(--color-bg-2);
        padding: 8px;
        overflow: hidden;
      }

      .card-list {
        height: 100%;
        overflow-y: auto;
        padding-right: 4px;

        /* Custom scrollbar for better look in consistent list */
        &::-webkit-scrollbar {
          width: 6px;
          height: 6px;
        }
        &::-webkit-scrollbar-thumb {
          border-radius: 3px;
          background-color: var(--color-fill-3);
        }
        &::-webkit-scrollbar-track {
          background-color: transparent;
        }

        .placeholder-text {
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: var(--color-text-4);
        }
      }

      .card-item {
        display: flex;
        gap: 12px;
        padding: 12px;
        border: 1px solid var(--color-border-2);
        border-radius: 4px;
        margin-bottom: 12px;
        cursor: pointer;
        transition: all 0.2s;
        background: var(--color-bg-1);

        &:hover {
          border-color: rgb(var(--primary-5));
        }

        &.active {
          border-color: rgb(var(--primary-6));
          background-color: var(--color-primary-light-1);
        }

        .card-icon {
          padding: 10px 16px;
          background: #fff;
          border: 1px solid var(--color-border-2);
          border-radius: var(--border-radius-small);
          display: flex;
          align-items: center;
          justify-content: center;
          img {
            width: 28px;
            height: 28px;
          }
        }

        .card-content {
          flex: 1;
          min-width: 0;
          display: flex;
          flex-direction: column;
          gap: 4px;

          &.full-width {
            width: 100%;
          }

          .card-title {
            font-weight: 500;
            color: var(--color-text-1);
            font-size: 14px;
          }

          .card-desc {
            color: var(--color-text-3);
            font-size: 12px;
            line-height: 1.4;

            display: -webkit-box;
            -webkit-line-clamp: 2;
            line-clamp: 2;
            -webkit-box-orient: vertical;
            overflow: hidden;
            text-overflow: ellipsis;
          }

          .card-meta {
            color: var(--color-text-4);
            font-size: 12px;
            margin-top: 4px;
          }
        }
      }
    }
  }
</style>
